import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildSubmissionReceiptsView,
  formatReceiptTime,
  receiptsFootnoteKey,
  receiptsSectionTitleKey,
  type SubmissionReceiptEntry,
  type SubmissionReceiptsSectionProps,
} from '../../lib/submission-receipts';

/**
 * "What you sent" (candidate) / "Submission receipts" (employer), web twin of
 * `SubmissionReceiptsSection.tsx` (audit E03). Same builder, same rules:
 * newest first, the current one marked, summaries only (nothing here opens a
 * document), an honest label for a row that predates receipts, nothing at all
 * when the payload carries no receipt fields.
 *
 * Web design system: section header 18/500, flat tinted rows, neutral badge,
 * logical alignment only, user text `dir="auto"`.
 */

const sectionStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  marginTop: 16,
  textAlign: 'start',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  paddingInline: 8,
};

const rowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '14px 18px',
  borderRadius: 18,
  backgroundColor: 'rgba(0,0,0,0.04)',
};

const factLabel: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
};

const factValue: CSSProperties = {
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 500,
  color: 'var(--text-primary)',
  wordBreak: 'break-word',
};

function Fact({
  label,
  value,
  userText = false,
}: {
  label: string;
  value: string;
  userText?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={factLabel}>{label}</span>
      {userText ? (
        <span style={factValue} dir="auto">
          {value}
        </span>
      ) : (
        <span style={factValue}>{value}</span>
      )}
    </div>
  );
}

export function SubmissionReceiptsSection({
  fields,
  audience,
  recipient,
}: SubmissionReceiptsSectionProps) {
  const { t, i18n } = useTranslation('common');
  const view = useMemo(() => buildSubmissionReceiptsView(fields), [fields]);

  if (view.state === 'unavailable') return null;

  const footnoteKey = receiptsFootnoteKey(audience, view);
  const count = view.state === 'recorded' ? view.entries.length : 0;

  const entryRow = (entry: SubmissionReceiptEntry) => {
    const language =
      'key' in entry.language ? t(entry.language.key) : entry.language.text;
    return (
      <>
        {entry.headingKey ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--text-primary)',
              }}
            >
              {t(entry.headingKey)}
            </span>
            {entry.current ? (
              <span className="tabbio-menu-badge">
                {t('submissionReceipts.current')}
              </span>
            ) : null}
          </div>
        ) : null}
        <Fact
          label={t('submissionReceipts.fields.cv')}
          value={
            entry.cvTitle ??
            t(entry.cvFallbackKey ?? 'submissionReceipts.cv.none')
          }
          userText={Boolean(entry.cvTitle)}
        />
        {entry.resumeFilename ? (
          <Fact
            label={t('submissionReceipts.fields.file')}
            value={entry.resumeFilename}
            userText
          />
        ) : null}
        <Fact label={t('submissionReceipts.fields.language')} value={language} />
        <Fact
          label={t('submissionReceipts.fields.sentAt')}
          value={formatReceiptTime(entry.submittedAt, i18n.language)}
        />
        <Fact
          label={t('submissionReceipts.fields.source')}
          value={t(entry.sourceKey)}
        />
      </>
    );
  };

  const rows: { key: string; node: ReactNode }[] = [];
  if (audience === 'candidate' && recipient) {
    rows.push({
      key: 'recipient',
      node: (
        <Fact
          label={t('submissionReceipts.recipient')}
          value={recipient}
          userText
        />
      ),
    });
  }
  if (view.state === 'historical') {
    rows.push({
      key: 'historical',
      node: (
        <>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--text-primary)',
            }}
          >
            {t('submissionReceipts.historical.title')}
          </span>
          <span
            style={{
              fontSize: 13,
              lineHeight: '19px',
              color: 'var(--text-secondary)',
            }}
          >
            {t('submissionReceipts.historical.body')}
          </span>
        </>
      ),
    });
  } else {
    for (const entry of view.entries) {
      rows.push({ key: entry.id, node: entryRow(entry) });
    }
  }

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <span
          style={{
            fontSize: 18,
            fontWeight: 500,
            color: 'var(--text-primary)',
          }}
        >
          {t(receiptsSectionTitleKey(audience))}
        </span>
        {count > 1 ? (
          <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>
            {t('submissionReceipts.sentCount', { count })}
          </span>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((row) => (
          <div key={row.key} style={rowStyle}>
            {row.node}
          </div>
        ))}
      </div>
      {footnoteKey ? (
        <span
          style={{
            fontSize: 12,
            lineHeight: '18px',
            color: 'var(--text-tertiary)',
            paddingInline: 8,
          }}
        >
          {t(footnoteKey)}
        </span>
      ) : null}
    </section>
  );
}
